import React, { useRef, useEffect, useState, useCallback } from 'react';
import remarkGfm from 'remark-gfm';
import InfiniteScroll from 'react-infinite-scroll-component';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

import { humanizeTokens } from '../../../../../../../../utils/utils';
import { useAskAicaSurface, ASK_AICA_SURFACE } from '../../context/AskAicaSurfaceContext';

import { errorIconRed, POKER_CHIP_ICON } from '../../../../../../../../assets/hostedassets';
import { Button } from '../../../../../../../../common/_custom';
import MagicUploadIcon from '../../../../../../../../assets/Svg/MagicuploadIcon';
import CopyIcon from '../../../../../../../../assets/Svg/CopyIcon';
import TickIcon from '../../../../../../../../assets/Svg/TickIcon';
import Progressbar from '../../../../../../../../common/Progressbar/Progressbar';
import useCopyText from '../../../../../../../../common/_custom/Hooks/useCopyText/useCopyText';
import {
    ASK_AICA_WORKING_COPY,
    isInProgressStatus,
    MessageType,
    MESSAGE_STATUS,
} from '../../../../../../../../store/dashboard/askAica/constants';
import { Message } from '../../../../../../../../store/dashboard/askAica/actions';
import { downloadAskAicaFile } from '../../../../../../../../store/dashboard/askAica/apiHelper';
import { isClarificationRoundSettled } from '../../../../../../../../store/dashboard/askAica/clarificationRound';
import { AskAicaFileRef } from '../../hooks/useAskAicaFileActions';

import FileAttachmentCard from '../FileAttachmentCard/FileAttachmentCard';
import { ClarificationTranscriptEntry } from '../ClarificationCard/ClarificationNote';
import AnswerScopeLine from './AnswerScopeLine';
import CitationAnswer from './CitationAnswer';
import FeedbackAckBubble from './FeedbackAckBubble';
import { CitationSource, OnCitationActivate } from './MessagesContainer.types';
import { SOURCE_HIGHLIGHT_DURATION_MS } from './MessagesContainer.constants';

import styles from './MessagesContainer.module.scss';

/** DOM id prefix for a message bubble, targeted by cross-chat citation focus
 *  (RECUR-27466 follow-up) — distinct from SOURCE_ANCHOR_PREFIX, which anchors
 *  a SourcesPanel entry, not the message bubble itself. */
export const MESSAGE_ANCHOR_PREFIX = 'msg-';

const formatSkillName = (skill: string) =>
    skill
        .split(/[-_]/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

/**
 * "Thinking" header — icon, animated label, ellipsis. Shown above the
 * reasoning box while a turn has no answer text yet, on BOTH the live and
 * re-attach paths — the two used to render entirely different markup here
 * (RECUR-28136), which is what made the box look different after a refresh.
 */
const ThinkingHeader: React.FC = () => (
    <div className={styles.TypingIndicator}>
        <div className={styles.IconShimmerContainer}>
            <MagicUploadIcon colour="#545B62" height="20" width="20" />
        </div>
        <span className={styles.ThinkingText} style={{ marginLeft: '16px' }}>
            Thinking
        </span>
        <span className={styles.Ellipsis}></span>
    </div>
);

/**
 * The reasoning box: skill chips, the accumulated reasoning text, and the
 * progress bar. Rendered by the single thinking-state block below (RECUR-28136
 * — live and re-attach used to render this through two different paths that
 * kept drifting apart; there is now exactly one call site).
 */
const ReasoningBox: React.FC<{
    message: string;
    skills?: string[];
    progressPercent: number;
    scrollRef?: React.RefObject<HTMLDivElement>;
}> = ({ message, skills, progressPercent, scrollRef }) => (
    <div className={styles.ReasoningContainer} ref={scrollRef} data-testid="ask-aica-inline-reasoning">
        <SkillChips skills={skills} />
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message}</ReactMarkdown>
        <Progressbar width={progressPercent} height={6} color="#000000" />
    </div>
);

/**
 * Whether the cost-weighted token-usage pill should render for a message:
 * an assistant reply that isn't an error, isn't the still-streaming placeholder,
 * and carries a positive, finite agg_tokens value.
 */
const shouldShowTokenUsage = (message: Message, isError: boolean, isStreamingPlaceholder: boolean): boolean => {
    const aggTokens = message.metadata?.agg_tokens;
    return (
        message.type === MessageType.ASSISTANT &&
        !isError &&
        !isStreamingPlaceholder &&
        typeof aggTokens === 'number' &&
        Number.isFinite(aggTokens) &&
        aggTokens > 0
    );
};

const SkillChips: React.FC<{ skills?: string[] }> = ({ skills }) => {
    if (!skills?.length) return null;
    return (
        <div className={styles.SkillChips} data-testid="skill-chips">
            {skills.map((skill) => (
                <span key={skill} className={styles.SkillChip}>
                    ✦ {formatSkillName(skill)}
                </span>
            ))}
        </div>
    );
};

interface MessagesContainerProps {
    isLoadingChatHistory: boolean;
    hasMoreData: boolean;
    messagesEndRef: React.RefObject<HTMLDivElement>;
    fetchChatHistory: Function;
    streamStarted?: boolean;
    retryMessage?: Function;
    scrollToBottom?: boolean;
    initialFetchRef: React.MutableRefObject<boolean>;
    containerRef?: React.RefObject<HTMLDivElement>;
    /** Opens a generated file in the matching in-app viewer (docx/xlsx/pdf). */
    onOpenFile: (file: AskAicaFileRef) => void;
    /** Snapshots a generated file into Reports (docx/xlsx/txt). Absent when the reports.addToReports visibility key is off. */
    onAddFileToReports?: (file: AskAicaFileRef) => void;
    /** URL of the file currently being opened — shows a spinner on that chat attachment card. */
    openingFileUrl?: string | null;
    /** Resolves + executes what a citation click should do (open doc / navigate section / scroll). */
    onCitationActivate: OnCitationActivate;
    /** Affordance-only tooltip hint for a citation (resolves the live section label). */
    getCitationTitle: (source: CitationSource) => string;
    // Mobile-only override (RECUR-28136): the mobile screen renders this component
    // with no AskAicaSurfaceProvider mounted, so useAskAicaSurface() alone can never
    // give it a working reattachExhausted — it passes its own useAskAica() instance's
    // value directly instead. Desktop leaves this undefined and reads the surface
    // context — mirrors AskAicaInputBox's stopRun/hasInProgressTurn override.
    reattachExhausted?: boolean;
}

const MessagesContainer: React.FC<MessagesContainerProps> = ({
    messagesEndRef,
    isLoadingChatHistory,
    hasMoreData,
    fetchChatHistory,
    streamStarted,
    retryMessage,
    scrollToBottom,
    initialFetchRef,
    containerRef,
    onOpenFile,
    onAddFileToReports,
    openingFileUrl,
    onCitationActivate,
    getCitationTitle,
    reattachExhausted: reattachExhaustedProp,
}) => {
    const internalContainerRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = containerRef || internalContainerRef;
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const reasoningScrollableDivRef = useRef<HTMLDivElement>(null);
    const contentRefsMap = useRef<Map<number | string, HTMLElement>>(new Map());

    const {
        messages,
        reasoning,
        isLoading,
        fileContextOrgId,
        surface,
        pinCompany,
        isViewOnly,
        hasPendingClarification,
        focusMessageId,
        clearFocusMessageId,
        reattachExhausted: surfaceReattachExhausted,
    } = useAskAicaSurface();
    const reattachExhausted = reattachExhaustedProp ?? surfaceReattachExhausted;
    const { copiedId, copyDirectText, copyElement } = useCopyText();

    const registerContentRef = (messageId: number | string) => (el: HTMLElement | null) => {
        if (el) contentRefsMap.current.set(messageId, el);
    };

    const handleCopyMessage = useCallback(
        (messageId: number | string, messageType: string, messageContent: string) => {
            if (messageType === MessageType.USER) {
                copyDirectText(messageContent, messageId);
                return;
            }
            const el = contentRefsMap.current.get(messageId);
            if (el) copyElement(el, messageId);
        },
        [copyDirectText, copyElement],
    );

    // A re-attached turn is "in progress" without this tab's own isLoading ever
    // being true (that flag only tracks a send this tab itself started) — the
    // progress bar's trigger below has to cover both (RECUR-28136).
    const isNewestAssistantInProgress = messages[0]?.type === MessageType.ASSISTANT && isInProgressStatus(messages[0]?.status);

    // The newest turn has no answer text yet, whether it's this tab sending it
    // live or re-attaching to one started elsewhere — the single condition the
    // thinking-state block below gates on, so there is one source of truth for
    // "is this the turn that block owns" instead of the flag re-derived inline.
    const isNewestTurnThinking = (isLoading || isNewestAssistantInProgress) && !messages[0]?.content;

    // A re-attached turn this tab did NOT start — the only case the LEAVE_SAFE/
    // EXHAUSTED sentence belongs to ("come back later" only makes sense to
    // someone who wasn't already watching it happen). A LIVE turn (isLoading)
    // shows the Thinking header alone until reasoning arrives — rendering the
    // sentence there was regression 1 (RECUR-28136).
    const isReattachedTurn = !isLoading && isNewestAssistantInProgress;

    // Download must run under the same context org the chat used. Leads: the
    // route org id (fileContextOrgId mirrors it exactly). Portfolio: there is no
    // route org id — fileContextOrgId is the gate-resolved currentCompany.org_id.
    const { id: routeOrgId } = useParams();
    const contextOrgId = fileContextOrgId ?? routeOrgId;

    const handleDownloadFile = useCallback(
        async (fileUrl: string, fileName: string) => {
            try {
                await downloadAskAicaFile(fileUrl, fileName, contextOrgId);
            } catch (err) {
                console.error('File download failed:', err);
                toast.error('Failed to download the file. Please try again.');
            }
        },
        [contextOrgId],
    );

    const [previousScrollHeight, setPreviousScrollHeight] = useState(0);
    const [shouldMaintainScrollPosition, setShouldMaintainScrollPosition] = useState(false);
    const [progressPercent, setProgressPercent] = useState(0);
    const prevNewestMessageIdRef = useRef<number | string | null>(null);
    const prevIsLoadingRef = useRef<boolean>(isLoading);
    const prevPendingRoundIdsRef = useRef<Set<number | string>>(new Set());

    // Clean up stale content refs when messages change
    useEffect(() => {
        const currentIds = new Set(messages.map((m) => m.id));
        contentRefsMap.current.forEach((_, id) => {
            if (!currentIds.has(id)) contentRefsMap.current.delete(id);
        });
    }, [messages]);

    useEffect(() => {
        if (shouldMaintainScrollPosition && messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            const newScrollHeight = container.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;

            // Adjust scroll position to maintain view
            container.scrollTop = container.scrollTop + scrollDifference;

            // Reset flags
            setShouldMaintainScrollPosition(false);
            setPreviousScrollHeight(0);
        }
    }, [messages, shouldMaintainScrollPosition, previousScrollHeight]);

    // Auto-scroll to the newest message: on the initial history load, whenever a
    // genuinely new message appears (new question or new assistant turn — messages[0]
    // changes identity), and when streaming finishes (the placeholder's content is
    // finalized without its id changing). Pagination (loading OLDER history) appends
    // to the far end of the array (see initMessageThread), so messages[0] never
    // changes there and this intentionally does not fire on it.
    useEffect(() => {
        if (!messages.length) return;

        const newestMessageId = messages[0].id;
        const isNewestMessage = newestMessageId !== prevNewestMessageIdRef.current;
        const streamingJustCompleted = prevIsLoadingRef.current && !isLoading;
        // A round settling swaps an older message's slim line for the taller Q&A note and unmounts the
        // docked card, a layout shift that can interrupt the smooth scroll started for the answer.
        const pendingRoundIds = new Set(
            messages
                .filter(
                    (message) =>
                        message.metadata?.clarification && !isClarificationRoundSettled(message.metadata.clarification.questions_state),
                )
                .map((message) => message.id),
        );
        const roundJustSettled = [...prevPendingRoundIdsRef.current].some((id) => !pendingRoundIds.has(id));

        if (initialFetchRef.current || isNewestMessage || streamingJustCompleted || roundJustSettled) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            initialFetchRef.current = false;
        }

        prevNewestMessageIdRef.current = newestMessageId;
        prevIsLoadingRef.current = isLoading;
        prevPendingRoundIdsRef.current = pendingRoundIds;
    }, [messages, isLoading]);

    // Scroll to bottom when scrollToBottom prop is true
    useEffect(() => {
        if (scrollToBottom && messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [scrollToBottom]);

    useEffect(() => {
        // A turn is "in progress" here whether THIS tab is loading it (live) or
        // it is a re-attached turn this tab never sent (RECUR-28136) — the
        // shared ReasoningBox needs its progress bar animated on both.
        const turnInProgress = isLoading || isNewestAssistantInProgress;

        // Start progress animation when reasoning first appears and the turn is in progress
        if (reasoning?.message && turnInProgress && !progressIntervalRef.current) {
            setProgressPercent(0);
            const duration = 30000; // 30 seconds
            const targetPercent = 95;
            const intervalTime = 50; // Update every 50ms for smooth animation
            const increment = (targetPercent / duration) * intervalTime;

            const interval = setInterval(() => {
                setProgressPercent((prev) => {
                    const newPercent = prev + increment;
                    if (newPercent >= targetPercent) {
                        return targetPercent;
                    }
                    return newPercent;
                });
            }, intervalTime);

            progressIntervalRef.current = interval;
        }

        // Reset progress when the turn is no longer in progress
        if (!turnInProgress && progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
            // Complete to 100% before resetting
            setProgressPercent(100);
            setTimeout(() => setProgressPercent(0), 500); // Hold at 100% briefly before hiding
        }
    }, [reasoning, isLoading, isNewestAssistantInProgress]);

    // Cleanup interval on component unmount
    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (reasoningScrollableDivRef.current) {
            reasoningScrollableDivRef.current.scrollTop = reasoningScrollableDivRef.current.scrollHeight;
        }
    }, [reasoning?.message]);

    // Cross-chat citation navigation (RECUR-27466 follow-up): a citation click
    // into "Related Conversations" opens the target conversation via
    // openConversation, which stashes the cited message's id as focusMessageId.
    // Once that conversation's history has (re)loaded and the target message is
    // actually present, scroll to and highlight it, then clear the pending
    // focus so this doesn't re-fire on unrelated re-renders. Waits on
    // isLoadingChatHistory (not just messages.length) because a conversation
    // switch clears messages before the new history lands.
    useEffect(() => {
        if (!focusMessageId || isLoadingChatHistory) return;
        const target = messages.find((m) => String(m.id) === focusMessageId);
        if (!target) return;
        const el = document.getElementById(`${MESSAGE_ANCHOR_PREFIX}${focusMessageId}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add(styles.SourceHighlight);
            setTimeout(() => el.classList.remove(styles.SourceHighlight), SOURCE_HIGHLIGHT_DURATION_MS);
        }
        clearFocusMessageId?.();
    }, [focusMessageId, isLoadingChatHistory, messages, clearFocusMessageId]);

    return (
        <div className={styles.MessagesContainer} id="scrollableDiv" ref={messagesContainerRef}>
            <div ref={messagesEndRef} />
            <InfiniteScroll
                dataLength={messages.length}
                hasMore={hasMoreData}
                next={() => {
                    if (!isLoadingChatHistory && !isLoading && hasMoreData) {
                        messagesContainerRef.current && setPreviousScrollHeight(messagesContainerRef.current.scrollHeight);
                        setShouldMaintainScrollPosition(true);
                        fetchChatHistory(true);
                    }
                }}
                className={styles.InfiniteScroll}
                loader={false}
                inverse={true}
                scrollableTarget="scrollableDiv"
            >
                {/* The ONE thinking-state renderer, for the newest turn while it has no
                    answer text yet (isNewestTurnThinking), whether this tab is sending it
                    live or re-attaching to a turn started elsewhere. streamStarted is a
                    separate, live-only guard: it never flips for a re-attach, so it can't
                    be folded into isNewestTurnThinking itself. Both paths used to render
                    through separate trees (RECUR-28136); the per-message bubble renders
                    nothing for this same turn while it qualifies here (see showBubble) so
                    this is the only place it appears. */}
                {isNewestTurnThinking && !streamStarted && (
                    <div className={styles.ThinkingMessage}>
                        <div className={`${styles.Message} ${styles.AiMessage}`}>
                            <div className={styles.MessageContent}>
                                <div className={styles.MessageBubble}>
                                    <ThinkingHeader />
                                </div>
                            </div>
                        </div>
                        {reasoning?.message ? (
                            <ReasoningBox
                                message={reasoning.message}
                                skills={messages[0]?.metadata?.skills_invoked}
                                progressPercent={progressPercent}
                                scrollRef={reasoningScrollableDivRef}
                            />
                        ) : (
                            isReattachedTurn && (
                                <p className={styles.ThinkingText}>
                                    {reattachExhausted ? ASK_AICA_WORKING_COPY.EXHAUSTED : ASK_AICA_WORKING_COPY.LEAVE_SAFE}
                                </p>
                            )
                        )}
                    </div>
                )}
                {messages.map((message, index) => {
                    // The settled note already carries the question and the pick, so the answer turn's own bubble is redundant.
                    if (message.isClarificationAnswer) return null;
                    // Check if this is an error message
                    const isError = message.error || message.metadata?.error || message.status === MESSAGE_STATUS.ERROR;
                    const errorMessage = message.errorMessage || message.metadata?.errorMessage;
                    // Newest assistant message while its response is still streaming in.
                    const isStreamingPlaceholder = isLoading && index === 0 && message.type === MessageType.ASSISTANT;
                    // A turn whose answer is still being produced server-side. Keyed on
                    // the MESSAGE'S OWN status rather than its position, so it is right
                    // on both surfaces and for a re-attached turn that is not the live
                    // one (RECUR-28136). A legacy `streaming` row is deliberately not in
                    // that set, so it renders as the ordinary finished answer it is.
                    const isAssistant = message.type === MessageType.ASSISTANT;
                    // Index 0 reuses the outer isNewestAssistantInProgress rather than
                    // re-deriving isInProgressStatus(message.status) on the same message.
                    const isInProgress = index === 0 ? isNewestAssistantInProgress : isAssistant && isInProgressStatus(message.status);
                    // A turn the user stopped. Whatever it had already generated is
                    // kept — discarding it would be the same data loss this feature
                    // exists to prevent — and the notice goes BENEATH that text. An
                    // empty one is the notice alone.
                    const isStopped = isAssistant && message.status === MESSAGE_STATUS.PARTIAL;
                    // An empty COMPLETE row renders nothing at all, exactly as before the
                    // backend started writing a row for a turn that produced none — the
                    // only term that could otherwise show it is the container-wide
                    // streamStarted flag.
                    const isCompleteEmpty = isAssistant && !message.content && message.status === MESSAGE_STATUS.COMPLETE;
                    // A contentless in-progress turn is rendered EXCLUSIVELY by the
                    // thinking-state block above — showBubble stays false for it here so
                    // that block is the only thing on screen (RECUR-28136). Once the turn
                    // has content, `message.content` below already picks it up.
                    const showBubble =
                        message.type === MessageType.USER || message.content || isError || isStopped || (streamStarted && !isCompleteEmpty);
                    // Captured as a local (rather than re-reading message.metadata?.file_url inside the
                    // onCardClick/onAddToReports closures below) so TS narrows it to non-optional there.
                    const fileAttachment: AskAicaFileRef | null = message.metadata?.file_url
                        ? {
                              url: message.metadata.file_url,
                              name: message.metadata.file_name || 'document',
                              type: message.metadata.file_type,
                          }
                        : null;

                    return (
                        <div
                            key={message.id}
                            id={`${MESSAGE_ANCHOR_PREFIX}${message.id}`}
                            data-message-type={message.type}
                            className={`${styles.Message} ${message.type === MessageType.USER ? styles.UserMessage : styles.AiMessage} ${
                                isError ? styles.ErrorMessage : ''
                            }`}
                        >
                            <div className={styles.MessageContent}>
                                {showBubble && (
                                    <>
                                        <div className={`${styles.MessageBubble} ${isError ? styles.ErrorBubble : ''}`}>
                                            {message.type === MessageType.ASSISTANT ? (
                                                // Feedback ack — isFeedbackAck is the canonical flag (promoted by the reducer
                                                // from metadata.feedback_ack on live turns, set by history map on reload).
                                                // metadata.feedback_ack is kept as a fallback for test scenarios where the
                                                // message is constructed without passing through the reducer (A-14, A-17).
                                                message.isFeedbackAck || message.metadata?.feedback_ack ? (
                                                    <FeedbackAckBubble
                                                        content={message.content}
                                                        registerContentRef={(el) => contentRefsMap.current.set(message.id, el)}
                                                    />
                                                ) : isError ? (
                                                    <p className={styles.ErrorText}>
                                                        <div className={styles.Top}>
                                                            <img src={errorIconRed} alt="err" height={'20px'} />
                                                            {errorMessage || message.content}
                                                        </div>
                                                    </p>
                                                ) : (
                                                    <div className={styles.AssistantMessageContainer}>
                                                        <div className={styles.MagicUploadIcon}>
                                                            <MagicUploadIcon colour="#545B62" height="20" width="20" />
                                                        </div>
                                                        <div className={styles.AssistantMessageContent}>
                                                            <SkillChips skills={message.metadata?.skills_invoked} />
                                                            {/* Copy target excludes the skill chips above */}
                                                            {message.metadata?.clarification ? (
                                                                // A clarifying-question round replaces the answer text (which is only
                                                                // the fallback for old clients) with the Q&A note or a slim line.
                                                                <ClarificationTranscriptEntry
                                                                    card={message.metadata.clarification}
                                                                    isViewOnly={isViewOnly}
                                                                    awaitingUser={index === 0}
                                                                    registerContentRef={registerContentRef(message.id)}
                                                                />
                                                            ) : (
                                                                <CitationAnswer
                                                                    message={message}
                                                                    registerContentRef={registerContentRef(message.id)}
                                                                    onCitationActivate={onCitationActivate}
                                                                    getCitationTitle={getCitationTitle}
                                                                />
                                                            )}
                                                            {isStopped && (
                                                                <p className={styles.StoppedNotice}>{ASK_AICA_WORKING_COPY.STOPPED}</p>
                                                            )}
                                                            {/* Which companies this answer covered, and which written
                                                                names it could not (RECUR-27889). Portfolio-only: the
                                                                wire omits answer_scope on in-lead turns, and the
                                                                surface check makes that structural rather than
                                                                dependent on the backend keeping its side. Rendered
                                                                OUTSIDE CitationAnswer's copy target on purpose —
                                                                copying an answer should yield the answer, not its
                                                                scope chrome (same reasoning as SkillChips above). */}
                                                            {surface === ASK_AICA_SURFACE.PORTFOLIO &&
                                                                message.metadata?.answer_scope &&
                                                                !isStreamingPlaceholder && (
                                                                    <AnswerScopeLine
                                                                        answerScope={message.metadata.answer_scope}
                                                                        onPinCompany={pinCompany}
                                                                        disabled={isLoading || hasPendingClarification}
                                                                    />
                                                                )}
                                                            {/* This row only reaches isInProgress with content already showing
                                                                (see showBubble) — the contentless thinking phase renders
                                                                exclusively through the block above. This trailing ellipsis is
                                                                the "more is still coming" cue for that remaining case. */}
                                                            {isInProgress && (
                                                                <div className={styles.TypingIndicator}>
                                                                    <span className={styles.Ellipsis}></span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            ) : (
                                                // Feedback user messages render like any normal user message.
                                                <p>{message.content}</p>
                                            )}
                                        </div>
                                        {fileAttachment && (
                                            <FileAttachmentCard
                                                fileUrl={fileAttachment.url}
                                                fileName={fileAttachment.name}
                                                fileType={fileAttachment.type}
                                                onDownload={handleDownloadFile}
                                                onCardClick={() => onOpenFile(fileAttachment)}
                                                onAddToReports={onAddFileToReports ? () => onAddFileToReports(fileAttachment) : undefined}
                                                loading={!!openingFileUrl && openingFileUrl === fileAttachment.url}
                                            />
                                        )}
                                        {/* A turn still being generated has no "sent at" or copyable text
                                            yet — that footer belongs to a finished message (RECUR-28136). */}
                                        {!isInProgress && (
                                            <div className={styles.MessageMeta}>
                                                {shouldShowTokenUsage(message, isError, isStreamingPlaceholder) && (
                                                    <div className={styles.TokenGroup}>
                                                        <img src={POKER_CHIP_ICON} width={14} height={14} alt="" />
                                                        <span className={styles.TokenText}>
                                                            {humanizeTokens(message.metadata?.agg_tokens ?? 0)} tokens
                                                        </span>
                                                    </div>
                                                )}
                                                <span className={styles.Timestamp}>
                                                    {new Date(message.timestamp)
                                                        .toLocaleString('en-US', {
                                                            hour: 'numeric',
                                                            minute: '2-digit',
                                                            hour12: true,
                                                        })
                                                        .replace(',', '')}{' '}
                                                    {new Date(message.timestamp)
                                                        .toLocaleString('en-US', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric',
                                                        })
                                                        .replace(',', '')}
                                                </span>
                                                {!isError && !isStreamingPlaceholder && (
                                                    <button
                                                        className={styles.CopyButton}
                                                        onClick={() => handleCopyMessage(message.id, message.type, message.content)}
                                                        title="Copy to clipboard"
                                                    >
                                                        {copiedId === message.id ? (
                                                            <TickIcon width={14} height={14} color="#49A71B" />
                                                        ) : (
                                                            <CopyIcon width="14" height="14" colour="#6b7280" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {/* metadata.retryable === false means a re-send fails identically
                                            (e.g. an over-length prompt), so offering a retry would be
                                            wrong advice. Only ever ABSENT or false, so `!== false` keeps
                                            every existing error retryable exactly as before. */}
                                        {isError && index === 0 && retryMessage && message.metadata?.retryable !== false && (
                                            <Button
                                                text="Try Again"
                                                onClick={() => retryMessage(index)}
                                                style={{ marginTop: '0', marginLeft: 'auto', padding: '10px 16px' }}
                                                variant="purpleinverted"
                                                fit
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </InfiniteScroll>
        </div>
    );
};

export default MessagesContainer;
